import { useState } from 'react';

import { Box, SwipeableDrawer, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';
import IosShareIcon from '@mui/icons-material/IosShare';

import { ContentTypeRichTextContent } from '@/types/generated';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import { resolveTypographyVariantStyles } from '@/utils/resolveTypographyVariantStyles';
import TAGButton from '@/components/TAGButton';
import PullerTab from '@/components/PullerTab/PullerTab';

import {
	getContentContainerStyles,
	getImageWrapperStyles,
	getContainerStyles,
	shareContentStyles,
	socialMediaLinkStyles,
	drawerStyles,
	pullerBoxStyles,
	mobileIconContainer,
	mobileTitleStyles,
	shareIconStyles,
	desktopOnly,
	mobileOnly,
} from '../BlogDetailsPage.styles';
import BlogAuthorDetails from '../BlogAuthorDetails';
import SocialMediaShareLinks from '../SocialMediaShareLinks';
import BlogDetailsImageAssetContainer from '../BlogDetailsImageAssetContainer';
import BlogTags from '../BlogTags';
import { IBlogDetailsPage } from '../BlogDetailsPage';

interface IBlogDetailsPageStandardProps extends IBlogDetailsPage {
	theme: Theme;
	isSmallScreen: boolean;
}

export default function BlogDetailsPageStandard(props: Readonly<IBlogDetailsPageStandardProps>) {
	const { title, date, summary, text, author, imageAsset, blogImage, theme, isSmallScreen, tags } = props;

	const [openDrawer, setOpenDrawer] = useState(false);

	const formattedDate = format(new Date(date), 'MM.dd.yyyy');

	const responsiveTypeVariantSX = [
		resolveTypographyVariantStyles(theme, 'bodyMediumBook'),
		{
			[theme.breakpoints.up('md')]: {
				...resolveTypographyVariantStyles(theme, 'bodyLargeBook'),
			},
		},
	];
	return (
		<Box data-test-id="blog_detail_page_container" sx={getContainerStyles}>
			<Box sx={[getImageWrapperStyles, desktopOnly]}>
				<BlogDetailsImageAssetContainer
					blogImage={blogImage}
					imageAsset={imageAsset}
					usePreloader={true}
					isSmallScreen
				/>
			</Box>
			<Box data-test-id="blog_detail_page_content_container" sx={[getContentContainerStyles]} component="article">
				<Typography
					data-test-id="blog_detail_page_content_date"
					color="text.secondary"
					component="span"
					sx={responsiveTypeVariantSX}
				>
					{formattedDate}
				</Typography>

				<Typography
					color="text.primary"
					component="h1"
					textAlign={{ xs: 'center', md: 'left' }}
					sx={[
						resolveTypographyVariantStyles(theme, 'heroMedium'),
						{
							[theme.breakpoints.up('md')]: {
								...resolveTypographyVariantStyles(theme, 'heroLarge'),
							},
						},
					]}
				>
					{title}
				</Typography>
				{summary?.json && (
					<ContentTypeRichText
						content={summary as ContentTypeRichTextContent}
						dataTestId="blog_detail_page_content_summary_richtext"
						contentMaxWidth="100%"
						disableGutters={true}
						disablePaddingOn={['Top', 'Bottom']}
						backgroundColor="transparent"
						centerAlign={!!isSmallScreen}
						docProps={{ color: 'text.secondary', sx: responsiveTypeVariantSX }}
						containerSx={{ marginBottom: '0.75rem' }}
					/>
				)}
				<BlogTags tags={tags as Array<string>}></BlogTags>
				<Box sx={[getImageWrapperStyles, mobileOnly]}>
					<BlogDetailsImageAssetContainer
						blogImage={blogImage}
						imageAsset={imageAsset}
						usePreloader={false}
						isSmallScreen
					/>
				</Box>
				<ContentTypeRichText
					content={text as ContentTypeRichTextContent}
					dataTestId="blog_detail_page_content_text_richtext"
					contentMaxWidth="100%"
					disableGutters={true}
					disablePaddingOn={['Top', 'Bottom']}
					containerSx={{ marginTop: '0.75rem' }}
					backgroundColor="transparent"
				/>
				{author && <BlogAuthorDetails {...author} />}
				<Box sx={shareContentStyles}>
					<Typography
						data-test-id="share_article_title_text"
						variant={'header3'}
						color="text.primary"
						component="span"
					>
						{isSmallScreen ? 'Share this article' : 'Share'}
					</Typography>
					<Box sx={socialMediaLinkStyles()}>
						{isSmallScreen ? (
							<TAGButton
								size="S"
								variant="secondaryDefault"
								endIcon={<IosShareIcon sx={shareIconStyles} />}
								onClick={() => setOpenDrawer(true)}
								dataTestId="mobile_shareable_link_button"
							>
								Share
							</TAGButton>
						) : (
							<SocialMediaShareLinks isCompactDesign={false} />
						)}
					</Box>
				</Box>
			</Box>
			{isSmallScreen && (
				<SwipeableDrawer
					anchor="bottom"
					onClose={() => setOpenDrawer(false)}
					onOpen={() => setOpenDrawer(true)}
					open={openDrawer}
					sx={{ ...drawerStyles, ...mobileOnly(theme) }}
					data-test-id={'shareable_mobile_link_drawer'}
				>
					<Box sx={pullerBoxStyles}>
						<PullerTab />
					</Box>
					<Typography sx={mobileTitleStyles} variant={'header2'} color="text.primary" component="span">
						Share this article
					</Typography>
					<Box sx={mobileIconContainer}>
						<SocialMediaShareLinks />
					</Box>
				</SwipeableDrawer>
			)}
		</Box>
	);
}
